import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchBankTransactions,
  fetchClients,
  fetchEmployeesBrief,
  fetchFinanceExpenses,
  getApiErrorMessage,
  putFinanceExpensePayout,
  putFinanceLiabilityPayout,
  resolveBankTransaction,
} from "../../api";
import ErrorRetry from "../../components/ErrorRetry";
import Loading from "../../components/Loading";
import BankInbox from "./BankInbox";
import { formatBankDate, signedBankAmount } from "./BankInboxRow";
import FinanceTabs from "./FinanceTabs";
import { accountTitle, formatMoney, formatMonthShort } from "./financeUtils";
import useFinanceOverview from "./useFinanceOverview";

const BANK_INBOX_KINDS = ["service", "kontur", "expense", "liability", "payroll", "dismiss"];

function asList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function clientsFromOverview(overview) {
  const raw = overview?.clients;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  return raw
    .map((item) => ({
      id: item.id ?? item.client_id,
      name: item.name,
    }))
    .filter((item) => item.id != null && item.name);
}

function groupBankByDay(items) {
  const groups = [];
  const index = new Map();
  items.forEach((item) => {
    const day = item.paid_at ? String(item.paid_at).slice(0, 10) : "—";
    if (!index.has(day)) {
      const group = { day, items: [] };
      index.set(day, group);
      groups.push(group);
    }
    index.get(day).items.push(item);
  });
  return groups;
}

function compareAccounts(a, b) {
  return (Number(a.sort_order) - Number(b.sort_order)) || (Number(a.id) - Number(b.id));
}

function visibleFunds(accounts) {
  return [...(accounts || [])]
    .filter((item) => !item.archived && item.kind === "fund")
    .sort(compareAccounts);
}

function normalizeAccountTitle(title) {
  return String(title || "").trim().toLowerCase().replace(/ё/g, "е");
}

function mainFirmAccount(accounts) {
  const firm = [...(accounts || [])]
    .filter((item) => !item.archived && item.kind === "account" && item.owner === "firm")
    .sort(compareAccounts);
  return firm.find((item) => normalizeAccountTitle(item.title) === "расчетный") || firm[0] || null;
}

function linkAmountLabel(account, owner) {
  const link = (account.links || []).find((item) => item.owner === owner);
  return link ? formatMoney(link.balance) : "—";
}

function linkGapCopy(gap) {
  if (gap === "lina") return "нет р/с Лины";
  if (gap === "vitaliy") return "нет р/с Виталия";
  if (gap === "both") return "нет р/с Лины и Виталия";
  return null;
}

function MoneyCell({ value }) {
  const num = Number(value);
  const negative = Number.isFinite(num) && num < 0;
  return <td className={negative ? "fin-neg" : undefined}>{formatMoney(value)}</td>;
}

function ForecastTable({ months, funds, mainAccount }) {
  const inflowFunds = funds.filter((fund) => fund.pct != null);
  const endAccounts = [...funds, mainAccount].filter(Boolean);

  return (
    <table className="fin-forecast">
      <thead>
        <tr>
          <th>Статья</th>
          {months.map((row) => (
            <th key={row.month}>{formatMonthShort(row.month)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">Приход план</th>
          {months.map((row, index) => (
            <td key={row.month}>
              {formatMoney(row.income_plan)}
              {index === 0 && row.income_fact != null ? ` факт ${formatMoney(row.income_fact)}` : ""}
            </td>
          ))}
        </tr>
        {inflowFunds.map((fund) => (
          <tr key={`in-${fund.id}`}>
            <th scope="row">Приток → {fund.title}</th>
            {months.map((row) => (
              <MoneyCell key={row.month} value={row.inflows?.[String(fund.id)]} />
            ))}
          </tr>
        ))}
        <tr>
          <th scope="row">Приток → Свободно</th>
          {months.map((row) => (
            <MoneyCell
              key={row.month}
              value={mainAccount ? row.inflows?.[String(mainAccount.id)] : 0}
            />
          ))}
        </tr>
        <tr>
          <th scope="row">ЗП</th>
          {months.map((row) => (
            <MoneyCell key={row.month} value={row.outflows?.payroll} />
          ))}
        </tr>
        <tr>
          <th scope="row">Кредиты</th>
          {months.map((row) => (
            <MoneyCell key={row.month} value={row.outflows?.credits} />
          ))}
        </tr>
        <tr>
          <th scope="row">Расходы</th>
          {months.map((row) => (
            <MoneyCell key={row.month} value={row.outflows?.expenses} />
          ))}
        </tr>
        {endAccounts.map((account) => (
          <tr key={`end-${account.id}`}>
            <th scope="row">Конец → {account.title}</th>
            {months.map((row) => (
              <MoneyCell key={row.month} value={row.ends?.[String(account.id)]} />
            ))}
          </tr>
        ))}
        <tr>
          <th scope="row">На жизнь</th>
          {months.map((row) => (
            <MoneyCell key={row.month} value={row.life} />
          ))}
        </tr>
      </tbody>
    </table>
  );
}

const KIND_LABEL = {
  credit: "Кредит",
  expense: "Расход",
  payroll: "ЗП к выдаче",
};

function MonthOpsTable({ items }) {
  const groups = groupBankByDay(items);
  return (
    <table className="fin-forecast" aria-label="Операции за месяц">
      <thead>
        <tr>
          <th>Дата</th>
          <th>Контрагент</th>
          <th>Назначение</th>
          <th>Сумма</th>
        </tr>
      </thead>
      {groups.map((group) => (
        <tbody key={group.day}>
          <tr>
            <th scope="colgroup" colSpan={4}>{formatBankDate(group.day)}</th>
          </tr>
          {group.items.map((item) => (
            <tr key={item.id}>
              <td>{formatBankDate(item.paid_at)}</td>
              <td>{item.counterparty_name || "—"}</td>
              <td>{item.purpose || "—"}</td>
              <td>{signedBankAmount(item)}</td>
            </tr>
          ))}
        </tbody>
      ))}
    </table>
  );
}

export default function FinanceOverview() {
  const { month, setMonth, overview, loading, error, reload } = useFinanceOverview();
  const [actionError, setActionError] = useState("");
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxItems, setInboxItems] = useState([]);
  const [inboxOptions, setInboxOptions] = useState({
    clients: [],
    expenses: [],
    liabilities: [],
    employees: [],
  });
  const [inboxLoading, setInboxLoading] = useState(false);
  const [inboxError, setInboxError] = useState("");
  const [monthOps, setMonthOps] = useState(null);
  const [monthOpsLoading, setMonthOpsLoading] = useState(false);
  const [monthOpsError, setMonthOpsError] = useState("");

  const accounts = useMemo(() => overview?.accounts || [], [overview]);
  const funds = useMemo(() => visibleFunds(accounts), [accounts]);
  const mainAccount = useMemo(() => mainFirmAccount(accounts), [accounts]);
  const cards = useMemo(
    () => [...funds, mainAccount].filter(Boolean),
    [funds, mainAccount]
  );
  const hasPayrollFund = funds.some((fund) => fund.role === "payroll");
  const calendar = useMemo(
    () => [...(overview?.calendar || [])].sort((a, b) => Number(a.day) - Number(b.day)),
    [overview]
  );
  const months = overview?.forecast?.months || [];
  const bank = overview?.bank || null;

  useEffect(() => {
    setMonthOps(null);
    setMonthOpsError("");
  }, [month]);

  useEffect(() => {
    if (!inboxOpen) return undefined;
    let cancelled = false;
    const loadInbox = async () => {
      setInboxLoading(true);
      setInboxError("");
      try {
        const derivedClients = clientsFromOverview(overview);
        const [txs, expenses, employees, clients] = await Promise.all([
          fetchBankTransactions({ status: "unmatched" }),
          fetchFinanceExpenses().catch(() => []),
          fetchEmployeesBrief().catch(() => []),
          derivedClients ? Promise.resolve(derivedClients) : fetchClients().catch(() => []),
        ]);
        if (cancelled) return;
        setInboxItems(asList(txs));
        setInboxOptions({
          clients: asList(clients).map((item) => ({
            id: item.id ?? item.client_id,
            name: item.name,
          })),
          expenses: asList(expenses).map((item) => ({ id: item.id, title: item.title })),
          liabilities: (overview?.liabilities || []).map((item) => ({
            id: item.id,
            title: item.title,
          })),
          employees: asList(employees).map((item) => ({
            id: item.id,
            full_name: item.full_name,
          })),
        });
      } catch (err) {
        if (!cancelled) {
          setInboxError(getApiErrorMessage(err, "Не удалось загрузить операции банка"));
        }
      } finally {
        if (!cancelled) setInboxLoading(false);
      }
    };
    loadInbox();
    return () => {
      cancelled = true;
    };
  }, [inboxOpen, overview]);

  const togglePaid = async (item, paid) => {
    setActionError("");
    try {
      if (item.kind === "credit") {
        await putFinanceLiabilityPayout(item.id, { month, paid });
      } else if (item.kind === "expense") {
        await putFinanceExpensePayout(item.id, { month, paid });
      }
      await reload();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
    }
  };

  const handleBankResolve = async (id, body) => {
    await resolveBankTransaction(id, body);
    const txs = await fetchBankTransactions({ status: "unmatched" });
    setInboxItems(asList(txs));
    await reload();
  };

  const loadMonthOps = async () => {
    setMonthOpsLoading(true);
    setMonthOpsError("");
    try {
      const data = await fetchBankTransactions({ month });
      setMonthOps(asList(data));
    } catch (err) {
      setMonthOpsError(getApiErrorMessage(err, "Не удалось загрузить операции"));
    } finally {
      setMonthOpsLoading(false);
    }
  };

  return (
    <div className="page-container fin-page">
      <FinanceTabs month={month} onMonthChange={setMonth} title="Обзор" />
      {error && <ErrorRetry message={error} onRetry={() => reload()} />}
      {actionError && (
        <div className="login-error" role="status" aria-live="polite">{actionError}</div>
      )}
      {loading && !overview ? (
        <Loading text="Загрузка кассы…" />
      ) : overview ? (
        <>
          <section className="fin-section fin-answer" aria-label="Ответ месяца">
            <h3 className="fin-visually-hidden">Ответ месяца</h3>
            {overview.answer?.covered ? (
              <p className="fin-ok">Всё покрыто</p>
            ) : (
              <p className="fin-gap">
                Дефицит месяца {formatMoney(overview.answer?.deficit)}
              </p>
            )}
            <p>
              Остаётся на жизнь:{" "}
              <span className="fin-answer-life">{formatMoney(overview.answer?.life_now)}</span>
            </p>
            <small>свободный приток минус платежи со счетов, не остаток Расчётного</small>
            <small>
              <span translate="no">Контур</span> должны: {formatMoney(overview.kontur_due)}
            </small>
          </section>

          <section className="fin-section" aria-label="Фонды">
            <div className="fin-kpis">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className={`stat-card fin-fund-card${Number(card.shortfall) > 0 ? " fin-gap" : ""}`}
                >
                  <span className="fin-kpi-caption">{card.title}</span>
                  <strong>
                    {formatMoney(card.balance)}
                    {" → "}
                    {Number(card.end_m0) < 0 ? (
                      <span className="fin-neg">{formatMoney(card.end_m0)}</span>
                    ) : (
                      formatMoney(card.end_m0)
                    )}
                  </strong>
                  <small>
                    <span>Лина {linkAmountLabel(card, "lina")}</span>
                    <span>Виталий {linkAmountLabel(card, "vitaliy")}</span>
                  </small>
                  {linkGapCopy(card.link_gap) ? (
                    <small>{linkGapCopy(card.link_gap)}</small>
                  ) : null}
                  {Number(card.shortfall) > 0 ? (
                    <small className="fin-neg">не хватает {formatMoney(card.shortfall)}</small>
                  ) : null}
                </div>
              ))}
            </div>
            {!hasPayrollFund && (
              <p>
                <Link to={{ pathname: "/finance/accounts", search: `?month=${month}` }}>
                  Назначьте фонд для ЗП
                </Link>
              </p>
            )}
          </section>

          <div className="fin-split">
            <section className="fin-section" aria-labelledby="fin-calendar-title">
              <h3 id="fin-calendar-title">Платежи месяца</h3>
              {calendar.length === 0 ? (
                <p>Нет платежей в этом месяце</p>
              ) : (
                <ul className="fin-calendar">
                  {calendar.map((item, index) => (
                    <li key={`${item.kind}-${item.id}-${item.day}-${index}`} className="fin-calendar-row">
                      <span className="fin-calendar-date">{item.day ?? "—"}</span>
                      <span>{KIND_LABEL[item.kind] || item.kind}</span>
                      <span className="fin-calendar-title">{item.title}</span>
                      <span className="fin-calendar-amount">{formatMoney(item.amount)}</span>
                      <span className="fin-calendar-account">{accountTitle(accounts, item.account_id)}</span>
                      {item.kind === "credit" || item.kind === "expense" ? (
                        <label className="fin-check">
                          <input
                            type="checkbox"
                            checked={Boolean(item.paid)}
                            onChange={(e) => togglePaid(item, e.target.checked)}
                          />
                          Оплачен
                        </label>
                      ) : (
                        <Link to={{ pathname: "/finance/payroll", search: `?month=${month}` }}>
                          Зарплата
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {bank ? (
              <div>
                <section className="fin-section cp-inbox" aria-label="Не разобрано в банке">
                  <button
                    type="button"
                    className="cp-inbox-toggle"
                    aria-expanded={inboxOpen}
                    onClick={() => setInboxOpen((prev) => !prev)}
                  >
                    <span className="cd-row-chevron" aria-hidden="true">{inboxOpen ? "▾" : "▸"}</span>
                    <span>Не разобрано в банке ({bank.unmatched_count ?? 0})</span>
                  </button>
                  {inboxOpen && (
                    <>
                      {inboxError && (
                        <div className="login-error" role="status" aria-live="polite">{inboxError}</div>
                      )}
                      {inboxLoading ? (
                        <Loading text="Загрузка операций…" />
                      ) : (
                        <BankInbox
                          items={inboxItems}
                          kinds={BANK_INBOX_KINDS}
                          options={inboxOptions}
                          onResolve={handleBankResolve}
                          title="Не разобрано в банке"
                        />
                      )}
                    </>
                  )}
                </section>

                <section className="fin-section" aria-labelledby="fin-bank-moves-title">
                  <h3 id="fin-bank-moves-title">Движения за месяц</h3>
                  <div className="fin-kpis">
                    <div className="stat-card">
                      <span>Пришло</span>
                      <strong>{formatMoney(bank.month?.inflow)}</strong>
                    </div>
                    <div className="stat-card">
                      <span>Ушло</span>
                      <strong>{formatMoney(bank.month?.outflow)}</strong>
                    </div>
                    <div className="stat-card">
                      <span>Переводы</span>
                      <strong>{formatMoney(bank.month?.transfers)}</strong>
                    </div>
                  </div>
                  <button type="button" className="cd-sort-btn" onClick={loadMonthOps}>
                    Показать операции
                  </button>
                  {monthOpsError && (
                    <div className="login-error" role="status" aria-live="polite">{monthOpsError}</div>
                  )}
                  {monthOpsLoading ? <Loading text="Загрузка операций…" /> : null}
                  {monthOps ? (
                    <div className="fin-table-wrap">
                      <MonthOpsTable items={monthOps} />
                    </div>
                  ) : null}
                </section>
              </div>
            ) : null}
          </div>

          <section className="fin-section" aria-label="Прогноз">
            <div className="fin-table-wrap">
              <ForecastTable months={months} funds={funds} mainAccount={mainAccount} />
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
